import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Assessment as ReportIcon,
  School as SchoolIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Download as DownloadIcon,
  CheckCircle as CheckIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Reports: React.FC = () => {
  const navigate = useNavigate();

  const reportTemplates = [
    {
      id: 1,
      title: 'Education Level Reports',
      description: 'Comprehensive reports for Pre-Primary, Primary, and Secondary education levels with enrollment, dropout, and completion analysis',
      category: 'Education Levels',
      icon: <SchoolIcon color="primary" />,
      status: 'ready',
      dataSource: 'Real SIS Data',
      route: '/reports/education-levels',
      lastGenerated: new Date().toISOString().split('T')[0],
    },
    {
      id: 2,
      title: 'School Summary Report',
      description: 'Comprehensive overview of all schools by district with enrollment, teacher distribution, and performance metrics',
      category: 'Schools',
      icon: <SchoolIcon color="success" />,
      status: 'ready',
      dataSource: 'Real SIS Data',
      route: '/reports/school-summary',
      lastGenerated: new Date().toISOString().split('T')[0],
    },
    {
      id: 3,
      title: 'School Information Reports',
      description: 'School-level reports: enrollment by form & gender, repeaters, dropouts, disabilities, teachers, WASH, and more — matching MOE Excel format',
      category: 'School Reports',
      icon: <ExcelIcon color="success" />,
      status: 'ready',
      dataSource: 'Real SIS Data',
      route: '/reports/school-reports',
      lastGenerated: new Date().toISOString().split('T')[0],
    },
    {
      id: 4,
      title: 'Infrastructure Assessment',
      description: 'Analysis of school infrastructure and facilities',
      category: 'Infrastructure',
      icon: <ReportIcon color="secondary" />,
      status: 'development',
      dataSource: 'Coming Soon',
      route: null,
      lastGenerated: null,
    },
    {
      id: 5,
      title: 'Teacher Statistics',
      description: 'Teacher distribution and qualification analysis',
      category: 'Staff',
      icon: <ReportIcon color="warning" />,
      status: 'development',
      dataSource: 'Coming Soon',
      route: null,
      lastGenerated: null,
    },
  ];

  const recentReports = [
    {
      id: 1,
      name: 'Education Level Reports - Current Academic Year',
      type: 'Live Report',
      size: 'Real-time',
      generatedDate: new Date().toISOString().split('T')[0],
      status: 'Ready',
      route: '/reports/education-levels',
    },
    {
      id: 2,
      name: 'School Summary Report - All Districts',
      type: 'Live Report',
      size: 'Real-time',
      generatedDate: new Date().toISOString().split('T')[0],
      status: 'Ready',
      route: '/reports/school-summary',
    },
    {
      id: 3,
      name: 'School Information Reports - All Types',
      type: 'Live Report',
      size: 'Real-time',
      generatedDate: new Date().toISOString().split('T')[0],
      status: 'Ready',
      route: '/reports/school-reports',
    },
    {
      id: 4,
      name: 'Infrastructure Assessment Report',
      type: 'PDF',
      size: 'Coming Soon',
      generatedDate: null,
      status: 'In Development',
      route: null,
    },
  ];

  const getFileIcon = (type: string) => {
    if (type === 'Live Report') return <ReportIcon color="primary" />;
    return type === 'PDF' ? <PdfIcon color="error" /> : <ExcelIcon color="success" />;
  };

  const getStatusChip = (status: string) => {
    if (status === 'Ready') return <Chip label={status} color="success" size="small" icon={<CheckIcon />} />;
    if (status === 'In Development') return <Chip label={status} color="warning" size="small" icon={<ScheduleIcon />} />;
    return <Chip label={status} color="info" size="small" />;
  };

  const getReportStatusChip = (status: string) => {
    if (status === 'ready') return <Chip label="Ready" color="success" size="small" icon={<CheckIcon />} />;
    return <Chip label="In Development" color="warning" size="small" icon={<ScheduleIcon />} />;
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reports & Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Access comprehensive reports with real-time data from the SIS database
      </Typography>

      <Grid container spacing={3}>
        {/* Report Templates */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Available Reports
            </Typography>
            
            <Grid container spacing={2}>
              {reportTemplates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {template.icon}
                        <Box sx={{ ml: 1, flex: 1 }}>
                          <Typography variant="h6" component="div">
                            {template.title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip
                              label={template.category}
                              size="small"
                              variant="outlined"
                            />
                            {getReportStatusChip(template.status)}
                          </Box>
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {template.description}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Data Source: {template.dataSource}
                      </Typography>
                      {template.lastGenerated && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Last updated: {template.lastGenerated}
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions>
                      {template.route ? (
                        <Button 
                          size="small" 
                          variant="contained" 
                          startIcon={<ReportIcon />}
                          onClick={() => navigate(template.route)}
                        >
                          View Report
                        </Button>
                      ) : (
                        <Button size="small" variant="outlined" disabled>
                          Coming Soon
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<PdfIcon />}
                color="error"
              >
                Generate PDF Report
              </Button>
              <Button
                variant="contained"
                fullWidth
                startIcon={<ExcelIcon />}
                color="success"
              >
                Export to Excel
              </Button>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ReportIcon />}
              >
                Custom Report Builder
              </Button>
            </Box>
          </Paper>

          {/* Report Statistics */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Report Status
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Live Reports Available</Typography>
                <Typography variant="body2" fontWeight="bold" color="success.main">2</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">In Development</Typography>
                <Typography variant="body2" fontWeight="bold" color="warning.main">2</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Data Source</Typography>
                <Typography variant="body2" fontWeight="bold" color="primary.main">Real SIS</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Last Updated</Typography>
                <Typography variant="body2" fontWeight="bold">Today</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Recent Reports */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Recent Reports
            </Typography>
            
            <List>
              {recentReports.map((report) => (
                <ListItem
                  key={report.id}
                  divider
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {getStatusChip(report.status)}
                      {report.status === 'Ready' && report.route && (
                        <Button
                          size="small"
                          startIcon={<ReportIcon />}
                          variant="outlined"
                          onClick={() => navigate(report.route)}
                        >
                          View Report
                        </Button>
                      )}
                    </Box>
                  }
                >
                  <ListItemIcon>
                    {getFileIcon(report.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={report.name}
                    secondary={
                      <Box>
                        <Typography variant="caption" display="block">
                          {report.type} • {report.size} • Generated: {report.generatedDate}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Reports;